import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  UpdateNote,
  UpdateNoteDto,
} from 'src/app/update-notes/types/update-note';

/**
 * Super-admin CRUD for release notes. Named Admin* to avoid colliding with the user-facing
 * UpdateNoteService, which hits the non-admin endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminUpdateNoteService {
  private http = inject(HttpClient);
  readonly adminUrl = `${environment.apiUrl}/admin/update-notes`;

  private _notes = signal<UpdateNote[] | null>(null);
  readonly notes = computed(() => this._notes() ?? []);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  loadNotes(): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.http.get<UpdateNote[]>(this.adminUrl).subscribe({
      next: (notes) => {
        this._notes.set(notes);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this._notes.set([]);
        this.error.set(err.message ?? 'Failed to load update notes');
        console.error(err);
      },
    });
  }

  getNoteById(id: string) {
    return this.http.get<UpdateNote>(`${this.adminUrl}/${id}`);
  }

  createNote(data: UpdateNoteDto) {
    return this.http.post<UpdateNote>(this.adminUrl, data).pipe(
      tap((created) => this._notes.update((list) => [...(list ?? []), created])),
    );
  }

  updateNote(id: string, data: UpdateNoteDto) {
    return this.http.put<UpdateNote>(`${this.adminUrl}/${id}`, data).pipe(
      tap((updated) =>
        this._notes.update((list) =>
          (list ?? []).map((note) => (note.id === updated.id ? updated : note)),
        ),
      ),
    );
  }

  deleteNote(id: string) {
    return this.http.delete<void>(`${this.adminUrl}/${id}`).pipe(
      tap(() =>
        this._notes.update((list) =>
          (list ?? []).filter((note) => note.id !== id),
        ),
      ),
    );
  }
}
